/**
 * Modelo do Extrato de Premiação: o que cada ciclo mostra, calculado a partir do dado da API.
 *
 * A página antiga decidia isso com uma escada de comparações de data espalhada pelo JSX. As
 * regras abaixo são a transcrição dessa escada, ciclo a ciclo: nenhum card, letra, valor, texto
 * ou aviso muda. O que mudou é o lugar — uma função pura, testada por tabela — para que o
 * visual novo não precise conhecer a regra de nenhum ciclo.
 *
 * Os textos de ciclos fechados continuam literais de propósito (extrato de ciclo fechado é
 * documento que o funcionário já leu); os de galho aberto para cima saem das funções de
 * `aviso-pre-requisito.ts`, que acompanham a revisão da IN.
 */

import { getNamedDates } from 'services/utils';

import { resolveComposicaoExtrato } from './extrato-premiacao-cards';
import {
  textoNivelJogandoJuntoNaoAtingido,
  textoOndeConferirPreRequisito,
  textoRequisitoNaoAtingido,
} from './aviso-pre-requisito';

export type IdDoCartao =
  | 'jogandoJunto'
  | 'galeria'
  | 'bonusCultura'
  | 'bonusRealizacao'
  | 'realizacaoDestaque'
  | 'conversaoFaixa'
  | 'coletiva'
  | 'cultura'
  | 'catalogo';

/** Conteúdo de tooltip; o texto de cada chave mora em `components/TooltipDoExtrato.tsx`. */
export type IdDoTooltip =
  | 'jj-ate-2S2023'
  | 'jj-1S2024'
  | 'jj-2S2024'
  | 'galeria'
  | 'cultura'
  | 'realizacaoDestaque'
  | 'coletiva-1S2023'
  | 'coletiva-2S2023'
  | 'coletiva-1S2024'
  | 'coletiva-2S2025';

export interface Formula {
  base?: number;
  rotuloBase: string;
  multiplicador?: number;
  rotuloMultiplicador: string;
}

/** Item do "Ver mais": realização da Coletiva ou fase do Bônus Realização Destaque (1S2023). */
export interface DetalheDoCartao {
  chave: string;
  titulo: string;
  periodo?: string;
  nivel: string;
  realizacao: string;
  pontos: string;
}

export interface CartaoDoExtrato {
  id: IdDoCartao;
  letra?: string;
  titulo: string;
  /** Nome curto na legenda da barra. */
  nomeCurto: string;
  tooltip?: IdDoTooltip;
  formula?: Formula;
  descricao?: string;
  /** Valor exibido; ausente quando um aviso toma o lugar dele. */
  valor?: number;
  /** Sinal antes do valor (o catálogo é débito). */
  sinal?: '-';
  unidade: string;
  /** Linhas de aviso no lugar do valor (nível não atingido, pré-requisito do 2S2023…). */
  aviso?: string[];
  detalhes?: DetalheDoCartao[];
  /** Texto e valor do "Ver mais" da Galeria. */
  detalheGaleria?: { descricao: string; pontos: string };
  /** Percentual na composição; só nos cartões com letra, quando a soma é maior que 0. */
  percentual?: number;
}

export interface Extrato {
  tituloDaPagina: string;
  emApuracao: boolean;
  /** Botão "ACESSAR CATÁLOGO LIVELO" e caixa "Apoio" (1S2023). */
  catalogoLivelo: boolean;
  cartoes: CartaoDoExtrato[];
  rotuloDoTotal: string;
  tituloDoTotal: string;
  total: number;
  unidadeDoTotal: string;
  avisoDoTopo?: string[];
  avisoDoTotal?: string[];
}

export interface CicloDoExtrato {
  title?: string;
  date_start?: number;
}

interface BonusGaleria {
  pontos_bonus?: number;
  multiplicador?: number;
}

interface RealizacaoDaColetiva {
  fase: string;
  realizacao_id: string | number;
  realizacao_titulo: string;
  premiacao_coletiva: number;
}

/** Documento de `find/livelo_points` do ciclo: só os campos que o extrato lê. */
export interface LiveloPoints {
  nivel?: string;
  grupo?: string;
  status_points?: string;
  meus_pontos?: number;
  meus_pontos_sem_multiplicador?: number;
  multiplicador_meus_pontos?: number;
  jogando_junto?: number;
  /** Número no 1S2023; objeto do 2S2023 em diante. */
  bonus_galeria_reconhecimento?: number | BonusGaleria | null;
  bonus_destaques_cultura?: number;
  total_realizacoes?: number;
  meus_pontos_task?: number;
  meus_pontos_task_sem_multiplicador?: number;
  gfm_multiplicador?: number;
  pontos_faixa?: number;
  premiacao_coletiva?: { teto_pontos?: number; passou_teto?: boolean; detalhes?: RealizacaoDaColetiva[] };
  pontos_usados?: number;
  total_pontos_livelo?: number;
}

/** Fase de `catalog_extract.achiviment` (1S2023). */
export interface FaseLegada {
  _id: string;
  faseTitle: string;
  totalMissao: number;
  task: { title: string; start: number; end: number };
}

export interface EntradaDoExtrato {
  ciclo?: CicloDoExtrato;
  livelo?: LiveloPoints;
  tipoDeAcordo?: string;
  temResgate?: boolean;
  fasesLegadas?: FaseLegada[];
  /** `resultado_cultura_2.behavior` (1S2023 e 1S2024). */
  comportamentoCultura?: string;
}

/** Em que trecho do programa o ciclo começa. Cada fronteira de data é comparada só aqui. */
export interface PeriodoDoCiclo {
  antesDo2S2023: boolean;
  desde1S2024: boolean;
  desde2S2024: boolean;
  desde1S2025: boolean;
  desde2S2025: boolean;
  desde1S2026: boolean;
  /** Inclusive: o 1S2025 ainda é "Pontos Livelo". */
  ate1S2025: boolean;
}

export function periodoDoCiclo(dateStart: number): PeriodoDoCiclo {
  const d = getNamedDates();
  return {
    antesDo2S2023: dateStart < d.ciclo_2S2023,
    desde1S2024: dateStart >= d.ciclo_1S2024,
    desde2S2024: dateStart >= d.ciclo_2S2024,
    desde1S2025: dateStart >= d.ciclo_1S2025,
    desde2S2025: dateStart >= d.ciclo_2S2025,
    desde1S2026: dateStart >= d.ciclo_1S2026,
    ate1S2025: dateStart <= d.ciclo_1S2025,
  };
}

const doisDigitos = (n: number) => String(n).padStart(2, '0');

export function formatarPontos(valor?: number): string {
  if (!valor) return '0';
  return valor.toLocaleString('pt-BR');
}

function dataCurta(time: number): string {
  const d = new Date(time);
  return `${doisDigitos(d.getDate())}/${doisDigitos(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

const SELOS: Record<string, string> = {
  colaboracao: 'Colaboração',
  foco: 'Foco no Cliente',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

/** Nome do selo do Destaque da Cultura; vazio sem comportamento ou sem bônus. */
function seloDaCultura(comportamento: string | undefined, livelo?: LiveloPoints): string {
  if (!comportamento || !((livelo?.bonus_destaques_cultura ?? 0) > 0)) return '';
  return SELOS[comportamento] ?? '';
}

const TEXTO_CONEXAO_2S2024 =
  'Você não é público alvo do Joga Junto para premiação em Pontos Livelo, por ser participante do Prêmio Conexão. IN 403-1 7.3.3.2.';

const TEXTO_NAO_AVALIAVEL =
  'Você não cumpriu um dos pré-requisitos de participação do Programa Joga Junto BB 1S2024 descritos na IN 403-1 item 7.2.';

/** Nível da apreciação da Realização Destaque, pela pontuação da realização. */
function nivelDaApreciacao(pontos: number, antesDo2S2023: boolean): string {
  const tabela: Record<number, string> = antesDo2S2023
    ? {
        600: 'Etapa 3 apreciação: Bonificada',
        1200: 'Etapa 4 apreciação: Bonificada',
        2400: 'Etapa 5 apreciação: Bonificada',
        3600: 'Etapa Selo: Indicada Selo Realização Destaque',
      }
    : {
        2000: 'Nível 2 apreciação: Bonificada',
        7000: 'Nível 3 apreciação: Bonificada',
        13000: 'Nível Selo: Indicada Selo Realização Destaque',
      };
  return tabela[pontos] ?? '';
}

/** Prêmio em pontos Livelo por etapa do Bônus Realização Destaque do 1S2023. */
const PREMIO_POR_ETAPA_1S2023: Record<number, string> = { 600: '1.000', 1200: '2.000', 2400: '3.000', 3600: '4.000' };

function semNivelJogandoJunto(livelo?: LiveloPoints): boolean {
  return !livelo?.nivel || livelo.nivel === 'nenhum' || livelo.nivel === 'iniciante';
}

function ehConexao(livelo?: LiveloPoints): boolean {
  return typeof livelo?.grupo === 'string' && livelo.grupo.includes('CONEXAO');
}

function preRequisitoOuImpedido(livelo?: LiveloPoints): boolean {
  return livelo?.status_points === 'pre_requisito_nao_cumprido' || livelo?.status_points === 'impedido';
}

/** Linhas do aviso de pré-requisito dos ciclos 2S2024 em diante (topo e faixa do total). */
function avisoDePreRequisito(ciclo: CicloDoExtrato, periodo: PeriodoDoCiclo): string[] {
  if (periodo.desde2S2025) {
    return [textoRequisitoNaoAtingido(ciclo.date_start), textoOndeConferirPreRequisito(ciclo.title)];
  }
  if (periodo.desde1S2025) {
    return [
      'Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item 6.4.8.2.3.',
      'Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > Extrato de Premiação> Joga Junto BB 2025/1 > Indicadores.',
    ];
  }
  return [
    'Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item 7.3.8.',
    'Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > Extrato de Premiação> Joga Junto BB 2024/2 > Indicadores.',
  ];
}

function avisoDoTopo(ciclo: CicloDoExtrato, periodo: PeriodoDoCiclo, livelo?: LiveloPoints): string[] | undefined {
  if (!periodo.desde1S2024) return undefined;

  if (periodo.desde2S2024) {
    if (preRequisitoOuImpedido(livelo) || livelo?.grupo === 'NAO AVALIAVEL') return avisoDePreRequisito(ciclo, periodo);
    if (ehConexao(livelo)) return [TEXTO_CONEXAO_2S2024];
    return undefined;
  }

  if (livelo?.status_points === 'pre_requisito_nao_cumprido' || livelo?.grupo === 'NAO AVALIAVEL' || ehConexao(livelo)) {
    return [
      'Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item 7.2.7.',
      'Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > PDG > Joga Junto BB 2024/1 > Indicadores.',
    ];
  }
  return [
    'Os pontos livelo já foram creditados.',
    'Na próxima semana, a Livelo vai trazer uma promoção especial exclusiva para os funcionários participantes do Joga Junto BB.',
    'Aguarde!!👏👏',
  ];
}

function avisoDoTotal(ciclo: CicloDoExtrato, periodo: PeriodoDoCiclo, livelo?: LiveloPoints): string[] | undefined {
  if (periodo.desde2S2024) {
    if (preRequisitoOuImpedido(livelo)) return avisoDePreRequisito(ciclo, periodo);
    if (livelo?.grupo === 'NAO AVALIAVEL') return [TEXTO_NAO_AVALIAVEL];
    if (ehConexao(livelo)) return [TEXTO_CONEXAO_2S2024];
    return undefined;
  }

  if (periodo.desde1S2024) {
    if (livelo?.status_points === 'pre_requisito_nao_cumprido') {
      return [
        'Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item 7.2.7.',
        'Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > PDG> Joga Junto BB 2024/1 > Indicadores.',
      ];
    }
    if (livelo?.grupo === 'NAO AVALIAVEL') return [TEXTO_NAO_AVALIAVEL];
    if (ehConexao(livelo)) {
      return [
        'Você faz parte do público-alvo do Prêmio Conexão 1S2024, sua premiação no Joga Junto se limitará à concessão de selos no humanograma e selos digitais conquistados nas dinâmicas da Galeria do Reconhecimento, Destaque da Cultura ou Realização Destaque que recebeu o Selo (IN 403-1 item 7.2.3.4).',
      ];
    }
  }
  return undefined;
}

/** Cartão "Jogando Junto": título, fórmula, valor ou aviso de cada ciclo. */
function cartaoJogandoJunto(
  ciclo: CicloDoExtrato,
  periodo: PeriodoDoCiclo,
  livelo?: LiveloPoints,
  letra?: string,
): CartaoDoExtrato {
  const naoPublico = livelo?.grupo === 'NAO AVALIAVEL' || ehConexao(livelo);

  if (periodo.desde2S2024) {
    const base: CartaoDoExtrato = {
      id: 'jogandoJunto',
      letra: letra ?? 'A',
      titulo: 'Premiação Pontos Jogando Junto',
      nomeCurto: 'Pontos Jogando Junto',
      tooltip: 'jj-2S2024',
      // Sem "Livelo" desde o 2S2024, como na página antiga; Cultura e catálogo mantêm "pontos Livelo" até o 1S2025.
      unidade: 'pontos adquiridos',
    };
    if (semNivelJogandoJunto(livelo) && !naoPublico) {
      return { ...base, aviso: [textoNivelJogandoJuntoNaoAtingido(ciclo.date_start)] };
    }
    return {
      ...base,
      valor: livelo?.meus_pontos,
      formula: {
        base: livelo?.meus_pontos_sem_multiplicador,
        rotuloBase: 'Pontos Joga Junto',
        multiplicador: livelo?.multiplicador_meus_pontos,
        rotuloMultiplicador: 'Nível de Reconhecimento',
      },
    };
  }

  if (periodo.desde1S2024) {
    const base: CartaoDoExtrato = {
      id: 'jogandoJunto',
      letra: 'B',
      titulo: 'Premiação Jogando Junto',
      nomeCurto: 'Jogando Junto',
      tooltip: 'jj-1S2024',
      unidade: 'pontos Livelo',
    };
    if (semNivelJogandoJunto(livelo) && !naoPublico) {
      return { ...base, aviso: ['Você não atingiu o nível Jogando Junto (IN 403-1 item 7.3.8.2.4.8.3.4)'] };
    }
    return { ...base, valor: livelo?.jogando_junto };
  }

  const legado: CartaoDoExtrato = {
    id: 'jogandoJunto',
    titulo: 'Pontos JogaJuntoBB (Meus pontos)',
    nomeCurto: 'Meus pontos',
    tooltip: 'jj-ate-2S2023',
    unidade: 'pontos adquiridos',
  };

  if (periodo.antesDo2S2023) return { ...legado, valor: livelo?.meus_pontos };

  // 2S2023: a ordem das verificações é a da página antiga.
  if (livelo?.status_points === 'pre_requisito_nao_cumprido') {
    return {
      ...legado,
      aviso: [
        'Você não atingiu o cumprimento de pelo menos um dos requisitos elencados na IN 403-1 item 8.1.7.2.',
        'Para verificar qual o pré-requisito não foi cumprido, você pode acessar Plataforma BB > Minha Visão > PDG > Joga Junto BB 2023/2 > Indicadores.',
      ],
    };
  }
  if (livelo?.grupo === 'SEM ATUAÇÃO MÍNIMA') {
    return {
      ...legado,
      aviso: [
        'Você não teve atuação mínima de 90 dias nos cargos/funções público-alvo do Joga Junto BB 2S2023 (IN 403-1 item 8.1.7.2.1)',
      ],
    };
  }
  if (ehConexao(livelo)) {
    return {
      ...legado,
      aviso: [
        'Você faz parte do público-alvo do Prêmio Conexão 2S2023, sua premiação no Joga Junto se limitará à concessão de selos no humanograma e selos digitais conquistados nas dinâmicas da Galeria do Reconhecimento, Destaque da Cultura ou Realização Destaque que recebeu o Selo (IN 403-1 item 8.1.3.4)',
      ],
    };
  }
  if (semNivelJogandoJunto(livelo)) {
    return { ...legado, aviso: ['Você não atingiu o nível Jogando Junto (IN 403-1 item 7.1.7.10)'] };
  }
  return { ...legado, valor: livelo?.meus_pontos };
}

const FAIXAS_1S2023: Record<number, { faixa: string; descricao: string }> = {
  2000: { faixa: 'Faixa 1', descricao: 'Você ficou entre os 10% mais bem colocados na pontuação geral atingindo a faixa 1.' },
  1000: { faixa: 'Faixa 2', descricao: 'Você ficou entre os 20% mais bem colocados na pontuação geral atingindo a faixa 2.' },
  500: { faixa: 'Faixa 3', descricao: 'Você ficou entre os 30% mais bem colocados na pontuação geral atingindo a faixa 3.' },
};

const FAIXAS_POR_MULTIPLICADOR: Record<number, string> = {
  3: 'Alcançou o nível #UAU, recebe "Meus Pontos" multiplicados por 3 (três)',
  2: 'Alcançou o nível #MandouBem, recebe "Meus Pontos" multiplicados por 2 (dois)',
  1.5: 'Alcançou o nível #Valeu, recebe "Meus Pontos" multiplicados por 1,5 (um e meio)',
  0: '',
};

/** Cartão da Galeria do Reconhecimento (1S2023 a 1S2024 no dado atual). */
function cartaoGaleria(periodo: PeriodoDoCiclo, livelo?: LiveloPoints): CartaoDoExtrato | undefined {
  const bonus = livelo?.bonus_galeria_reconhecimento;
  const bonusEmPontos = typeof bonus === 'number' ? bonus : undefined;
  const bonusDetalhado = typeof bonus === 'object' && bonus !== null ? bonus : undefined;
  // `Number()` repete a coerção do `>=` da página antiga (`pontos_bonus: null` conta como 0).
  if (!(periodo.antesDo2S2023 || Number(bonusDetalhado?.pontos_bonus) >= 0)) return undefined;

  // Como na página antiga, `null` (typeof "object") também soma os "Meus pontos".
  const valor =
    typeof bonus === 'object' ? (livelo?.meus_pontos ?? 0) + (bonus?.pontos_bonus ?? 0) : bonusEmPontos;

  let unidade = '';
  let descricao = '';
  if (periodo.antesDo2S2023) {
    const faixa = bonusEmPontos === undefined ? undefined : FAIXAS_1S2023[bonusEmPontos];
    if (faixa) {
      unidade = `pontos adquiridos na ${faixa.faixa}`;
      descricao = faixa.descricao;
    }
  } else if (bonusDetalhado?.multiplicador !== undefined && bonusDetalhado.multiplicador in FAIXAS_POR_MULTIPLICADOR) {
    // Na página antiga o "adquiridos na Faixa N" comparava o objeto com 0 e nunca aparecia.
    unidade = 'pontos Livelo';
    descricao = FAIXAS_POR_MULTIPLICADOR[bonusDetalhado.multiplicador];
  }

  const temVerMais = (bonusEmPontos ?? 0) > 0 || (bonusDetalhado?.pontos_bonus ?? 0) > 0;

  return {
    id: 'galeria',
    // Do 2S2024 em diante a Jogando Junto também é "A" (herdado da página antiga; PREM-L04).
    letra: periodo.antesDo2S2023 ? undefined : 'A',
    titulo: periodo.desde1S2024 ? 'Premiação Galeria do Reconhecimento' : 'Bônus Galeria de Reconhecimento',
    nomeCurto: 'Galeria do Reconhecimento',
    tooltip: periodo.desde1S2024 ? 'galeria' : undefined,
    valor,
    unidade,
    detalheGaleria: temVerMais ? { descricao, pontos: `+ ${formatarPontos(valor)} pts Livelo` } : undefined,
  };
}

/** Bônus Destaques da Cultura e Bônus Realização Destaque, só do 1S2023. */
function cartoesDeBonus1S2023(
  livelo: LiveloPoints | undefined,
  fasesLegadas: FaseLegada[] | undefined,
  comportamentoCultura: string | undefined,
): CartaoDoExtrato[] {
  const selo = seloDaCultura(comportamentoCultura, livelo);
  return [
    {
      id: 'bonusCultura',
      titulo: 'Bônus Destaques da Cultura',
      nomeCurto: 'Destaques da Cultura',
      descricao: selo ? `Pelo Selo ${selo}` : undefined,
      valor: livelo?.bonus_destaques_cultura,
      unidade: 'pontos adquiridos',
    },
    {
      id: 'bonusRealizacao',
      titulo: 'Bônus Realização Destaque',
      nomeCurto: 'Realização Destaque',
      tooltip: 'coletiva-1S2023',
      descricao: 'Bonificações por fases',
      valor: livelo?.total_realizacoes,
      unidade: 'pontos Livelo adquiridos',
      // Sem fase, sem "Ver mais": a página antiga mostrava o botão e não abria nada.
      detalhes: fasesLegadas?.length
        ? fasesLegadas.map(item => ({
            chave: String(item._id),
            titulo: item.faseTitle,
            periodo: `Do dia ${dataCurta(item.task.start)} ao ${dataCurta(item.task.end)}`,
            nivel: nivelDaApreciacao(item.totalMissao, true),
            realizacao: `Nome da Realização: ${item.task.title}`,
            pontos: `+ ${PREMIO_POR_ETAPA_1S2023[item.totalMissao] ?? 0} pts Livelo`,
          }))
        : undefined,
    },
  ];
}

function cartaoRealizacaoDestaque(livelo: LiveloPoints | undefined, letra = 'B'): CartaoDoExtrato {
  return {
    id: 'realizacaoDestaque',
    letra,
    titulo: 'Premiação Pontos Realização Destaque',
    nomeCurto: 'Realizações Destaque',
    tooltip: 'realizacaoDestaque',
    formula: {
      base: livelo?.meus_pontos_task_sem_multiplicador,
      rotuloBase: 'Pontos Realização Destaque',
      // Truncado como na página antiga (PREM-L03).
      multiplicador: (livelo?.gfm_multiplicador ?? 0) | 0,
      rotuloMultiplicador: 'Multiplicador GFM',
    },
    valor: livelo?.meus_pontos_task,
    unidade: 'pontos adquiridos',
  };
}

function cartaoConversaoFaixa(livelo: LiveloPoints | undefined, letra?: string): CartaoDoExtrato {
  return {
    id: 'conversaoFaixa',
    letra,
    titulo: 'Conversão da Faixa de Premiação em Espécie',
    nomeCurto: 'Conversão da Faixa',
    valor: livelo?.pontos_faixa,
    unidade: 'pontos adquiridos',
  };
}

function detalhesDaColetiva(livelo: LiveloPoints | undefined, unidade: string): DetalheDoCartao[] | undefined {
  const realizacoes = livelo?.premiacao_coletiva?.detalhes ?? [];
  if (!realizacoes.length) return undefined;
  return realizacoes.map(item => ({
    chave: String(item.realizacao_id),
    titulo: item.fase,
    nivel: nivelDaApreciacao(item.premiacao_coletiva, false),
    realizacao: `Nome da Realização: ${item.realizacao_titulo}`,
    pontos: `+ ${formatarPontos(item.premiacao_coletiva)} ${unidade}`,
  }));
}

/** Coletiva do 2S2023 e do 1S2024 (no 2S2024 e no 1S2025 não há Coletiva). */
function cartaoColetivaAte1S2024(periodo: PeriodoDoCiclo, livelo?: LiveloPoints): CartaoDoExtrato {
  return {
    id: 'coletiva',
    letra: periodo.desde1S2024 ? 'C' : 'B',
    titulo: 'Premiação Coletiva',
    nomeCurto: 'Coletiva',
    tooltip: periodo.desde1S2024 ? 'coletiva-1S2024' : 'coletiva-2S2023',
    descricao: 'Bonificações por fases conquistadas pela sua unidade',
    valor: livelo?.premiacao_coletiva?.teto_pontos,
    unidade: 'pontos Livelo adquiridos',
    detalhes: detalhesDaColetiva(livelo, 'pts Livelo'),
  };
}

/** Coletiva do 2S2025 em diante. */
function cartaoColetiva(livelo: LiveloPoints | undefined, letra = 'D'): CartaoDoExtrato {
  return {
    id: 'coletiva',
    letra,
    titulo: 'Premiação Coletiva',
    nomeCurto: 'Coletiva',
    tooltip: livelo?.premiacao_coletiva?.passou_teto ? 'coletiva-2S2025' : undefined,
    descricao: 'Bonificações por fases conquistadas pela sua unidade',
    valor: livelo?.premiacao_coletiva?.teto_pontos,
    unidade: 'pontos adquiridos',
    detalhes: detalhesDaColetiva(livelo, 'pontos'),
  };
}

/** Destaques da Cultura do 1S2024 em diante. */
function cartaoCultura(
  periodo: PeriodoDoCiclo,
  livelo: LiveloPoints | undefined,
  comportamentoCultura: string | undefined,
  letra: string | undefined,
  unidade: string,
): CartaoDoExtrato {
  const antesDo2S2024 = !periodo.desde2S2024;
  const selo = seloDaCultura(comportamentoCultura, livelo);
  return {
    id: 'cultura',
    letra: antesDo2S2024 ? 'D' : letra ?? 'C',
    titulo: antesDo2S2024 ? 'Premiação Destaques da Cultura BB' : 'Premiação Destaques da Cultura',
    nomeCurto: 'Destaques da Cultura',
    tooltip: 'cultura',
    descricao: selo
      ? `Por ter recebido um Destaque da Cultura com o Selo ${selo}`
      : 'Bônus fixo de 2.000 pontos para quem é Destaque da Cultura no ciclo',
    valor: livelo?.bonus_destaques_cultura,
    unidade,
  };
}

function cartaoCatalogo(livelo: LiveloPoints | undefined, unidade: string): CartaoDoExtrato {
  return {
    id: 'catalogo',
    titulo: 'Catálogo de experiências',
    nomeCurto: 'Catálogo',
    descricao: 'Pontos já utilizados em resgates no catálogo',
    valor: livelo?.pontos_usados,
    sinal: '-',
    unidade,
  };
}

/** Monta o extrato de um ciclo. Sem ciclo, devolve só o cabeçalho (Rede ou carregando). */
export function montarExtrato({
  ciclo,
  livelo,
  tipoDeAcordo,
  temResgate = false,
  fasesLegadas,
  comportamentoCultura,
}: EntradaDoExtrato): Extrato {
  const dateStart = ciclo?.date_start;
  if (!ciclo || typeof dateStart !== 'number') {
    return {
      tituloDaPagina: 'Extrato de Premiação',
      emApuracao: false,
      catalogoLivelo: false,
      cartoes: [],
      rotuloDoTotal: '',
      tituloDoTotal: '',
      total: 0,
      unidadeDoTotal: '',
    };
  }

  const periodo = periodoDoCiclo(dateStart);
  const composicao = resolveComposicaoExtrato({ dateStart, tipoDeAcordo });
  const pontos = periodo.ate1S2025 ? 'pontos Livelo' : 'pontos';
  const cartoes: CartaoDoExtrato[] = [];
  const add = (cartao?: CartaoDoExtrato) => cartao && cartoes.push(cartao);

  // Galeria e Jogando Junto: a partir do 1S2024 a Galeria vem antes (ordem invertida).
  const jj = cartaoJogandoJunto(ciclo, periodo, livelo, composicao.letras.jogandoJunto);
  const galeria = cartaoGaleria(periodo, livelo);
  if (periodo.desde1S2024) {
    add(galeria);
    add(jj);
  } else {
    add(jj);
    add(galeria);
  }

  if (periodo.antesDo2S2023) cartoes.push(...cartoesDeBonus1S2023(livelo, fasesLegadas, comportamentoCultura));
  if (periodo.desde2S2024 && tipoDeAcordo === 'HIBRIDO') add(cartaoRealizacaoDestaque(livelo, composicao.letras.realizacaoDestaque));
  if (composicao.cards.includes('conversaoFaixa')) add(cartaoConversaoFaixa(livelo, composicao.letras.conversaoFaixa));
  if (!periodo.antesDo2S2023 && !periodo.desde2S2024) add(cartaoColetivaAte1S2024(periodo, livelo));
  if (periodo.desde1S2024) add(cartaoCultura(periodo, livelo, comportamentoCultura, composicao.letras.cultura, pontos));

  const mostraColetiva = !composicao.novaComposicao || composicao.cards.includes('coletiva');
  if (periodo.desde2S2025 && mostraColetiva) add(cartaoColetiva(livelo, composicao.letras.coletiva));
  if (temResgate && periodo.desde1S2025) add(cartaoCatalogo(livelo, pontos));

  aplicarPercentuais(cartoes);

  return {
    tituloDaPagina: periodo.ate1S2025 ? 'Pontos Livelo' : 'Extrato de Premiação',
    emApuracao: periodo.desde1S2026,
    catalogoLivelo: periodo.antesDo2S2023,
    cartoes,
    rotuloDoTotal: rotuloDoTotal(periodo, composicao),
    tituloDoTotal: periodo.ate1S2025 ? 'Total de Pontos Livelo a creditar' : 'Total de Pontos a creditar',
    total: livelo?.total_pontos_livelo ?? 0,
    unidadeDoTotal: pontos,
    avisoDoTopo: avisoDoTopo(ciclo, periodo, livelo),
    avisoDoTotal: avisoDoTotal(ciclo, periodo, livelo),
  };
}

function rotuloDoTotal(periodo: PeriodoDoCiclo, composicao: ReturnType<typeof resolveComposicaoExtrato>): string {
  if (composicao.novaComposicao) return composicao.rotuloTotal;
  if (!periodo.antesDo2S2023 && !periodo.desde2S2025) {
    if (periodo.desde2S2024) return '(A + B + C )';
    if (periodo.desde1S2024) return '(A + B + C + D)';
    return '(A + B)';
  }
  return '(A + B + C + D)';
}

/** Percentual de cada cartão com letra sobre a soma deles; sem soma, ninguém recebe percentual. */
function aplicarPercentuais(cartoes: CartaoDoExtrato[]) {
  const comLetra = cartoes.filter(c => c.letra && typeof c.valor === 'number');
  const soma = comLetra.reduce((total, c) => total + (c.valor ?? 0), 0);
  if (soma <= 0) return;
  comLetra.forEach(c => {
    c.percentual = ((c.valor ?? 0) / soma) * 100;
  });
}

/** "24,6%", como na legenda e nos chips do design. */
export function formatarPercentual(valor: number): string {
  return `${valor.toFixed(1).replace('.', ',')}%`;
}
